import {
  GetNarrativeClustersTable,
  type GetNarrativeClustersTableRow,
  type GetNarrativeClustersTableVariables,
} from "@/data-access/discovery-view/queries/discovery-view-queries";
import { client } from "@/lib/urqlProvider";
import { transformNarrativeClusterToRowData } from "@/store/discovery/discovery-content-management/discovery-content-management.helpers";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import type { IDatasource, IGetRowsParams } from "ag-grid-community";
import type { NarrativeClusterRowData } from "./NarrativeClustersTableStates";

const fetchNarrativeClustersForSSRM = async (
  projectionFilters: GetNarrativeClustersTableVariables["projectionFilters"],
  filters: GetNarrativeClustersTableVariables["filters"],
  ordering: GetNarrativeClustersTableVariables["ordering"],
  limit: number,
  offset: number,
): Promise<{
  narrativeClusters: GetNarrativeClustersTableRow[];
  totalCount: number;
}> => {
  const result = await client
    .query(
      GetNarrativeClustersTable,
      {
        projectionFilters,
        filters,
        pagination: { limit, offset },
        ordering: ordering ?? [],
      },
      {
        requestPolicy: "network-only",
      },
    )
    .toPromise();

  if (result.error) {
    throw new Error(result.error.message);
  }

  if (!result.data?.narrativeClusterTable) {
    return { narrativeClusters: [], totalCount: 0 };
  }

  const { totalCount, results: narrativeClusters } =
    result.data.narrativeClusterTable;

  return {
    narrativeClusters,
    totalCount: Number(totalCount ?? 0),
  };
};

/**
 * Module-level mutable state for session-based race-condition prevention.
 * AG Grid creates new datasource instances when filters/search/ordering change;
 * in-flight requests from the previous datasource must not update loading state
 * or overwrite totalCount. These counters track session identity and request order.
 */
let currentSessionId = 0;
let inFlightCountForCurrentSession = 0;
let latestFetchId = 0;

export const startNewDatasourceSession = () => {
  currentSessionId += 1;
  inFlightCountForCurrentSession = 0;
};

export const createNarrativeClustersDatasource = (
  getProjectionFilters: () => GetNarrativeClustersTableVariables["projectionFilters"],
  getFilters: () => GetNarrativeClustersTableVariables["filters"],
  searchTerm: string,
  ordering: GetNarrativeClustersTableVariables["ordering"],
): IDatasource => {
  return {
    getRows: async (params: IGetRowsParams) => {
      const sessionId = currentSessionId;
      const fetchId = ++latestFetchId;
      inFlightCountForCurrentSession += 1;

      const startRow = params.startRow ?? 0;
      const endRow = params.endRow ?? 0;

      const {
        setIsFetchingNarrativeClustersTableData,
        setIsNarrativeClustersInitialLoading,
        setNarrativeClustersTableError,
        setTotalNarrativeClustersCount,
      } = useDiscoveryContentManagementStore.getState();

      const isFirstBatch = startRow === 0;
      if (isFirstBatch) {
        setIsNarrativeClustersInitialLoading(true);
      }

      const baseProjectionFilters = getProjectionFilters();
      const projectionFilters: GetNarrativeClustersTableVariables["projectionFilters"] =
        {
          ...baseProjectionFilters,
          ...(searchTerm ? { searchMatch: searchTerm } : {}),
        };
      const filters = getFilters();
      const defaultPageSize = 50;
      let limit = defaultPageSize;
      if (endRow > startRow) {
        limit = endRow - startRow;
      } else if (endRow === 0 && startRow === 0) {
        limit = defaultPageSize;
      }
      const offset = startRow;

      try {
        setIsFetchingNarrativeClustersTableData(true);
        setNarrativeClustersTableError(null);

        const { narrativeClusters, totalCount } =
          await fetchNarrativeClustersForSSRM(
            projectionFilters,
            filters,
            ordering,
            limit,
            offset,
          );

        const rowData: NarrativeClusterRowData[] = narrativeClusters.map(
          transformNarrativeClusterToRowData,
        );

        if (fetchId !== latestFetchId) {
          if (sessionId === currentSessionId) {
            inFlightCountForCurrentSession -= 1;
            if (inFlightCountForCurrentSession === 0) {
              setIsFetchingNarrativeClustersTableData(false);
              if (isFirstBatch) setIsNarrativeClustersInitialLoading(false);
            }
          }
          params.failCallback();
          return;
        }

        setTotalNarrativeClustersCount(totalCount);

        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setIsFetchingNarrativeClustersTableData(false);
            if (isFirstBatch) setIsNarrativeClustersInitialLoading(false);
          }
        }

        params.successCallback(rowData, totalCount);
      } catch (error: unknown) {
        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setIsFetchingNarrativeClustersTableData(false);
            if (isFirstBatch) setIsNarrativeClustersInitialLoading(false);
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            setNarrativeClustersTableError(errorMessage);
          }
        }
        params.failCallback();
      }
    },
  };
};
